import { useState, useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// EditSiteModal
// Props:
//   site     – object matching the site schema (or null for a blank form)
//   onSave   – (updatedSite) => void
//   onClose  – () => void
// ---------------------------------------------------------------------------
export default function EditSiteModal({ site = null, onSave, onClose, codeDisabled=true }) {
  let initialForm = null;
  
  if (site === null ) {
    initialForm = {
      code: "",
      name: "",
      region: "",
      active: false,
      locked: false,
    };
  } else {
    initialForm = {
      code: site.code ?? "",
      name: site.name ?? "",
      region: site.region ?? "",
      active: site.active ?? false,
      locked: site.locked ?? false,
    };
  }

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const firstInputRef = useRef(null);

  // Populate form when a site is passed in
  useEffect(() => {
    if (site) {
      setForm({
        code: site.code ?? "",
        name: site.name ?? "",
        region: site.region ?? "",
        active: site.active ?? false,
        locked: site.locked ?? false,
      });
    }
  }, [site]);

  // Trap focus on mount
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // ── Validation ─────────────────────────────────────────────────────────────
  function validate(data) {
    const errs = {};
    if (!data.code.trim()) {
      errs.code = "Code is required.";
    } else if (data.code.trim().length > 25) {
      errs.code = "Code must be 25 characters or fewer.";
    }
    if (!data.name.trim()) {
      errs.name = "Name is required.";
    } else if (data.name.trim().length > 50) {
      errs.name = "Name must be 50 characters or fewer.";
    }
    if (data.region.trim().length > 50) {
      errs.region = "Region must be 50 characters or fewer.";
    }
    return errs;
  }

  // ── Handlers ───────────────────────────────────────────────────────────────
  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSave({
      ...form,
      code: form.code.trim(),
      name: form.name.trim(),
      region: form.region.trim(),
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  const inputBase =
    "w-full rounded-md border px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 " +
    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";

  const inputValid = "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700";
  const inputInvalid = "border-red-400 bg-red-50";

  function fieldClass(field) {
    return `${inputBase} ${errors[field] ? inputInvalid : inputValid}`;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Panel */}
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 shadow-xl ring-1 ring-slate-200 dark:ring-slate-700">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 px-6 py-4">
          <div>
            <h2
              id="modal-title"
              className="text-base font-semibold text-slate-800 dark:text-slate-100"
            >
              {site ? "Edit Site" : "New Site"}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {site ? `Updating ${site.code}` : "Fill in the details below"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
            aria-label="Close"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 2l12 12M14 2L2 14" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5 px-6 py-5">

            {/* Code */}
            <div>
              <label
                htmlFor="code"
                className="mb-1.5 block text-xs font-medium text-slate-600 uppercase tracking-wide"
              >
                Code <span className="text-red-500">*</span>
              </label>
              <input
                ref={firstInputRef}
                id="code"
                name="code"
                type="text"
                maxLength={25}
                value={form.code}
                disabled={codeDisabled}
                onChange={handleChange}
                placeholder="e.g. SITE-001"
                className={fieldClass("code")}
                aria-describedby={errors.code ? "code-error" : undefined}
                aria-invalid={!!errors.code}
              />
              <div className="mt-1 flex items-center justify-between">
                {errors.code ? (
                  <p id="code-error" className="text-xs text-red-500">{errors.code}</p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-slate-400">{form.code.length}/25</span>
              </div>
            </div>

            {/* Name */}
            <div>
              <label
                htmlFor="name"
                className="mb-1.5 block text-xs font-medium text-slate-600 uppercase tracking-wide"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                maxLength={50}
                value={form.name}
                onChange={handleChange}
                placeholder="Full site name"
                className={fieldClass("name")}
                aria-describedby={errors.name ? "name-error" : undefined}
                aria-invalid={!!errors.name}
              />
              <div className="mt-1 flex items-center justify-between">
                {errors.name ? (
                  <p id="name-error" className="text-xs text-red-500">{errors.name}</p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-slate-400">{form.name.length}/50</span>
              </div>
            </div>

            {/* Region */}
            <div>
              <label
                htmlFor="region"
                className="mb-1.5 block text-xs font-medium text-slate-600 uppercase tracking-wide"
              >
                Region
              </label>
              <input
                id="region"
                name="region"
                type="text"
                maxLength={50}
                value={form.region}
                onChange={handleChange}
                placeholder="e.g. Gauteng"
                className={fieldClass("region")}
                aria-describedby={errors.region ? "region-error" : undefined}
                aria-invalid={!!errors.region}
              />
              <div className="mt-1 flex items-center justify-between">
                {errors.region ? (
                  <p id="region-error" className="text-xs text-red-500">{errors.region}</p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-slate-400">{form.region.length}/50</span>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  name: "active",
                  label: "Active",
                  description: "Site is live and accessible",
                  color: "text-emerald-600",
                  ring: "focus:ring-emerald-500",
                  checkedStyle: "bg-emerald-500 border-emerald-500",
                },
                {
                  name: "locked",
                  label: "Locked",
                  description: "Prevent edits to this site",
                  color: "text-amber-600",
                  ring: "focus:ring-amber-500",
                  checkedStyle: "bg-amber-500 border-amber-500",
                },
              ].map(({ name, label, description, color, checkedStyle }) => (
                <label
                  key={name}
                  htmlFor={name}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition
                    ${form[name]
                      ? "border-slate-300 bg-slate-50"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                >
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input
                      id={name}
                      name={name}
                      type="checkbox"
                      checked={form[name]}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div
                      className={`h-4 w-4 rounded border-2 flex items-center justify-center transition
                        ${form[name] ? checkedStyle : "border-slate-300 bg-white"}`}
                    >
                      {form[name] && (
                        <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M1.5 5l2.5 2.5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${form[name] ? color : "text-slate-700"}`}>
                      {label}
                    </span>
                    <p className="text-xs text-slate-400 leading-snug mt-0.5">{description}</p>
                  </div>
                </label>
              ))}
            </div>

          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white
                         hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500
                         focus:ring-offset-2 transition"
            >
              Save Site
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
